import * as XLSX from "xlsx"

// ── Helpers ────────────────────────────────────────────────────────────────

function normalizeKey(k: string): string {
  return k.toLowerCase().trim().replace(/\s+/g, "_")
}

function cell(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key]
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim()
  }
  return ""
}

function readFile(file: File): Promise<XLSX.WorkBook> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer)
        resolve(XLSX.read(data, { type: "array" }))
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

function normalize(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((row) => {
    const r: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(row)) r[normalizeKey(k)] = v
    return r
  })
}

// ── Exercise import ────────────────────────────────────────────────────────

export interface ParsedExercise {
  name: string
  muscle_group: string
  equipment?: string
  description?: string
  gif_url?: string
  error?: string
}

export async function parseExercisesFromExcel(file: File): Promise<ParsedExercise[]> {
  const wb = await readFile(file)
  const sheet = wb.Sheets[wb.SheetNames[0]]
  const rows = normalize(XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" }))

  return rows
    .map((r) => {
      const name = cell(r, "nombre", "name")
      const muscle_group = cell(r, "grupo_muscular", "muscle_group", "musculo", "grupo")
      const equipment = cell(r, "equipo", "equipment") || undefined
      const description = cell(r, "descripcion", "description") || undefined
      const gif_url = cell(r, "gif_url", "gif", "url_gif") || undefined

      const errors: string[] = []
      if (!name) errors.push("Falta nombre")
      if (!muscle_group) errors.push("Falta grupo muscular")

      return { name, muscle_group, equipment, description, gif_url, error: errors[0] }
    })
    .filter((r) => r.name || r.muscle_group)
}

// ── Routine import ─────────────────────────────────────────────────────────

const DAY_MAP: Record<string, number> = {
  lunes: 1, monday: 1,
  martes: 2, tuesday: 2,
  "miércoles": 3, miercoles: 3, wednesday: 3,
  jueves: 4, thursday: 4,
  viernes: 5, friday: 5,
  "sábado": 6, sabado: 6, saturday: 6,
  domingo: 0, sunday: 0,
}

export interface ParsedRoutineExercise {
  exercise_name: string
  sets: number
  reps: number
  weight?: number
  tempo?: string
  notes?: string
}

export interface ParsedRoutine {
  name: string
  description?: string
  scheduled_day?: number
  exercises: ParsedRoutineExercise[]
}

export async function parseRoutinesFromExcel(file: File): Promise<ParsedRoutine[]> {
  const wb = await readFile(file)
  const sheet = wb.Sheets[wb.SheetNames[0]]
  const rows = normalize(XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" }))

  const grouped: Record<string, ParsedRoutine> = {}

  for (const r of rows) {
    const routineName = cell(r, "rutina", "routine", "nombre_rutina", "nombre")
    if (!routineName) continue

    if (!grouped[routineName]) {
      const dayStr = cell(r, "dia", "day", "dia_semana").toLowerCase()
      let scheduled_day: number | undefined
      if (dayStr in DAY_MAP) scheduled_day = DAY_MAP[dayStr]
      else if (/^[0-6]$/.test(dayStr)) scheduled_day = parseInt(dayStr)

      grouped[routineName] = {
        name: routineName,
        description: cell(r, "descripcion", "description") || undefined,
        scheduled_day,
        exercises: [],
      }
    }

    const exercise_name = cell(r, "ejercicio", "exercise", "nombre_ejercicio")
    if (!exercise_name) continue

    const weightStr = cell(r, "peso", "weight")
    grouped[routineName].exercises.push({
      exercise_name,
      sets: parseInt(cell(r, "series", "sets")) || 3,
      reps: parseInt(cell(r, "reps", "repeticiones")) || 10,
      weight: weightStr ? parseFloat(weightStr) : undefined,
      tempo: cell(r, "tempo") || undefined,
      notes: cell(r, "notas", "notes") || undefined,
    })
  }

  return Object.values(grouped)
}

// ── Template downloads ─────────────────────────────────────────────────────

export function downloadExerciseTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    ["nombre", "grupo_muscular", "equipo", "descripcion", "gif_url"],
    ["Sentadilla", "Legs", "Barra", "Sentadilla con barra trasera", ""],
    ["Press de Banca", "Chest", "Barra", "Press plano con barra", ""],
    ["Dominadas", "Back", "Barra", "Agarre prono", ""],
  ])
  ws["!cols"] = [{ wch: 25 }, { wch: 16 }, { wch: 18 }, { wch: 35 }, { wch: 40 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Ejercicios")
  XLSX.writeFile(wb, "plantilla_ejercicios.xlsx")
}

export function downloadRoutineTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    ["rutina", "descripcion", "dia", "ejercicio", "series", "reps", "peso", "tempo", "notas"],
    ["Día de Piernas", "Tren inferior", "Lunes", "Sentadilla", 4, 8, 100, "3-1-1-0", "ATG depth"],
    ["Día de Piernas", "", "", "Prensa de pierna", 3, 10, 150, "", ""],
    ["Día de Piernas", "", "", "Curl de pierna", 3, 12, "", "", ""],
    ["Día de Pecho", "Push day", "Miércoles", "Press de Banca", 4, 8, 80, "3-1-1-0", ""],
    ["Día de Pecho", "", "", "Aperturas", 3, 12, 20, "", ""],
  ])
  ws["!cols"] = [{ wch: 20 }, { wch: 20 }, { wch: 12 }, { wch: 22 }, { wch: 8 }, { wch: 6 }, { wch: 8 }, { wch: 10 }, { wch: 25 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Rutinas")
  XLSX.writeFile(wb, "plantilla_rutinas.xlsx")
}
